// プッシュ通知（この端末で受け取るか・種類）。送るのはサーバー（worker の push.ts・notify.ts）、表示は public/sw.js。
// iPhone・iPad はホーム画面に追加したアプリからだけ受け取れる（iOS 16.4 以降）
import { api } from '../api';
import { icon } from './icons';
import { openModal, toast } from './overlay';
import { isStandalone } from './pwa';

export const PUSH_KINDS = [
  ['news', '運営からのお知らせ'],
  ['present', 'プレゼント'],
  ['event', 'イベント'],
  ['village', '里がもうすぐ満杯'],
  ['farm', '閉じたあとのオート周回の結果'],
] as const;
const ALL = PUSH_KINDS.map(([k]) => k as string);

let keyP: Promise<string | null> | null = null;
/** サーバーの公開鍵（未設定なら null＝通知の設定を出さない） */
export const pushKey = () => (keyP ??= api.pushKey().then((r) => r.key, () => null));

const isIOS = () => /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
const hasPush = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window && window.isSecureContext;

/** ok＝受け取れる／install＝ホーム画面に追加すれば受け取れる（iPhone）／denied＝端末の設定で止められている／unsupported */
export function pushSupport(): 'ok' | 'install' | 'denied' | 'unsupported' {
  if (isIOS() && !isStandalone()) return 'install';
  if (!hasPush()) return 'unsupported';
  return Notification.permission === 'denied' ? 'denied' : 'ok';
}

async function currentSub() {
  const reg = await navigator.serviceWorker.getRegistration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/** この端末で受け取っている種類（受け取っていなければ null） */
export async function pushState(): Promise<string[] | null> {
  if (pushSupport() !== 'ok' || Notification.permission !== 'granted') return null;
  const sub = await currentSub();
  if (!sub) return null;
  return (await api.pushState(sub.endpoint)).kinds;
}

const keyBytes = (k: string) => Uint8Array.from(atob(k.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (k.length % 4)) % 4)), (c) => c.charCodeAt(0));

/** 受け取る（許可を聞く）。押した操作の中で呼ぶ（iPhone は操作の中でないと許可を聞けない） */
export async function enablePush(kinds: string[] = ALL): Promise<boolean> {
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return false;
  const key = await pushKey();
  if (!key) return false;
  const reg = await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
  await api.pushOn(sub.toJSON(), kinds);
  return true;
}

/** 受け取る種類を変える */
export async function setPushKinds(kinds: string[]) {
  const sub = await currentSub();
  if (sub) await api.pushOn(sub.toJSON(), kinds);
}

/** この端末では受け取らない */
export async function disablePush() {
  const sub = await currentSub();
  if (!sub) return;
  await api.pushOff(sub.endpoint).catch(() => undefined);
  await sub.unsubscribe().catch(() => false);
}

// ---------- 通知をすすめる（オート周回を始めたとき・里で受け取ったとき。アプリとして開いているときだけ） ----------
const ASK_KEY = 'hk.pushAsk';
type AskState = { until?: number; asked?: string[] };
const readAsk = (): AskState => {
  try {
    return JSON.parse(localStorage.getItem(ASK_KEY) ?? '{}') as AskState;
  } catch {
    return {};
  }
};
const writeAsk = (s: AskState) => {
  try {
    localStorage.setItem(ASK_KEY, JSON.stringify(s));
  } catch {
    /* 保存できなくても進める */
  }
};
/** 「今はしない」を押したら、この日数はどのきっかけでも出さない */
const SNOOZE_DAYS = 14;
const ASKS = {
  farm: { text: 'アプリを閉じても、オート周回の結果を通知でお知らせできます。', kinds: ['farm'] },
  village: { text: '里の蔵がもうすぐ満杯になったら、通知でお知らせできます。', kinds: ['village'] },
} as const;

/** 通知をすすめる（それぞれのきっかけで1回まで）。閉じたら終わる。受け取る種類は全部（設定で選び直せる） */
export async function suggestPush(kind: keyof typeof ASKS): Promise<void> {
  if (!isStandalone() || pushSupport() !== 'ok' || Notification.permission === 'denied') return;
  const ask = readAsk();
  if ((ask.until ?? 0) > Date.now() || ask.asked?.includes(kind)) return;
  if (!(await pushKey())) return;
  if (Notification.permission === 'granted' && (await pushState().catch(() => null)) != null) return;
  writeAsk({ ...ask, asked: [...(ask.asked ?? []), kind] });
  await new Promise<void>((done) => {
    const m = openModal(
      `<div class="push-ask">
        <img class="pwa-icon" src="/icons/icon-192.png" alt="" width="64" height="64">
        <p>${ASKS[kind].text}</p>
        <div class="btn-row"><button class="btn" data-later>今はしない</button><button class="btn primary" data-on>${icon('check')}受け取る</button></div>
      </div>`,
      { title: '通知', onClose: () => done() },
    );
    m.body.querySelector('[data-later]')!.addEventListener('click', () => {
      writeAsk({ ...readAsk(), until: Date.now() + SNOOZE_DAYS * 86_400_000 });
      m.close();
    });
    // 許可の確認は押したその流れの中で出す（iPhone は操作の中でないと聞けない）
    m.body.querySelector('[data-on]')!.addEventListener('click', () => {
      m.close();
      void enablePush().then(
        (ok) => toast(ok ? '通知を受け取ります（種類は設定から）' : '通知が許可されませんでした', ok ? 'good' : 'error'),
        () => toast('通知を設定できませんでした', 'error'),
      );
    });
  });
}
